
(() => {
  "use strict";

  const PRICE_REGEX = /^\s*[\d.,\s]+€\s*$/;

  const ADD_TO_CART =
    'button[aria-label="Adicionar ao carrinho"]';

  const CART =
    'button[aria-label="Carrinho"]';

  const PRODUCT_LINK =
    'a[href*="/products/"]';

  const CARD_ATTRIBUTE =
    "data-samurai-catalog-card";

  const initializedRoots = new WeakSet();
  let activeRoot = null;

  // ==========================================
  // 1. OCULTAR ELEMENTOS COMERCIAIS
  // ==========================================

  function injectStyles(root) {
    if (root.querySelector(
      "style[data-samurai-catalog-style]"
    )) return;

    const style = document.createElement("style");

    style.setAttribute(
      "data-samurai-catalog-style", ""
    );

    style.textContent = `
      ${ADD_TO_CART},
      ${CART} {
        display: none !important;
      }

      [${CARD_ATTRIBUTE}] {
        cursor: pointer !important;
      }
    `;

    root.appendChild(style);
  }

  // ==========================================
  // 2. OCULTAR PREÇOS
  // ==========================================

  function hidePrices(root) {
    root.querySelectorAll("*").forEach(el => {
      if (
        el.children.length === 0 &&
        PRICE_REGEX.test(el.textContent || "")
      ) {
        el.style.setProperty(
          "display",
          "none",
          "important"
        );
      }
    });
  }

  // ==========================================
  // 3. IDENTIFICAR LINKS DOS PRODUTOS
  // ==========================================

  function getProductUrl(link) {
    if (!link) return null;

    try {
      const url = new URL(
        link.getAttribute("href"),
        document.baseURI
      );

      if (
        url.origin !== window.location.origin ||
        !url.pathname.includes("/products/")
      ) {
        return null;
      }

      return url.href;
    } catch {
      return null;
    }
  }

  // ==========================================
  // 4. IDENTIFICAR O CARTÃO DO PRODUTO
  // ==========================================

  function findProductCard(link, root) {
    let element = link.parentElement;
    let fallback = null;

    for (
      let depth = 0;
      element && element !== root && depth < 10;
      depth++, element = element.parentElement
    ) {
      if (!element.querySelector("img")) {
        continue;
      }

      const productUrls = new Set(
        [...element.querySelectorAll(PRODUCT_LINK)]
          .map(getProductUrl)
          .filter(Boolean)
      );

      // Evitar selecionar um grupo de vários produtos
      if (productUrls.size > 1) {
        break;
      }

      if (productUrls.size !== 1) {
        continue;
      }

      const buyButtons =
        element.querySelectorAll(ADD_TO_CART);

      if (buyButtons.length > 1) {
        break;
      }

      if (buyButtons.length === 0) {
        continue;
      }

      const rect = element.getBoundingClientRect();

      if (!fallback) {
        fallback = element;
      }

      // Preferir o cartão com borda própria
      const css = getComputedStyle(element);

      const hasBorder =
        parseFloat(css.borderTopWidth) > 0 &&
        css.borderTopStyle !== "none";

      if (hasBorder) {
        return element;
      }

      // Alternativa para cartões sem borda
      if (
        rect.height >= 100 &&
        rect.height <= 350
      ) {
        fallback = element;
      }
    }

    return fallback;
  }

  // ==========================================
  // 5. TORNAR OS CARTÕES CLICÁVEIS
  // ==========================================

  function makeCardsClickable(root) {
    const links =
      root.querySelectorAll(PRODUCT_LINK);

    links.forEach(link => {
      const productUrl = getProductUrl(link);

      if (!productUrl) return;

      const card = findProductCard(link, root);

      if (!card) return;

      if (card.hasAttribute(CARD_ATTRIBUTE)) {
        return;
      }

      card.setAttribute(CARD_ATTRIBUTE, "true");
    });
  }

  // ==========================================
  // 6. ABRIR A PÁGINA DO PRODUTO
  // ==========================================

  function handleCardClick(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const card = event.target.closest(
      `[${CARD_ATTRIBUTE}]`
    );

    if (!card) return;

    // Manter o comportamento dos links e controlos
    if (
      event.target.closest(
        "a, button, input, select, textarea, " +
        "[role='button'], [role='link']"
      )
    ) {
      return;
    }

    const link = [
      ...card.querySelectorAll(PRODUCT_LINK)
    ].find(el => getProductUrl(el));

    if (!link) return;

    // Reutilizar o link original do produto
    link.click();
  }

  // ==========================================
  // 7. ATUALIZAR O CATÁLOGO
  // ==========================================

  function updateCatalog(root) {
    injectStyles(root);
    hidePrices(root);
    makeCardsClickable(root);
  }

  // ==========================================
  // 8. CONFIGURAR O ASSISTENTE
  // ==========================================

  function customizeAgent(root) {
    if (initializedRoots.has(root)) {
      return;
    }

    initializedRoots.add(root);
    activeRoot = root;

    updateCatalog(root);

    root.addEventListener(
      "click",
      handleCardClick
    );

    // Observar novos produtos enviados pela IA
    let pending = false;

    const observer = new MutationObserver(() => {
      if (pending) return;

      pending = true;

      requestAnimationFrame(() => {
        pending = false;
        updateCatalog(root);
      });
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true
    });

    console.log(
      "[Samurai] Catálogo IA personalizado."
    );
  }

  // ==========================================
  // 9. LOCALIZAR O WIDGET MICROSOFT
  // ==========================================

  function searchShadowRoots(scope = document) {
    const elements =
      scope.querySelectorAll("*");

    for (const element of elements) {
      const root = element.shadowRoot;

      if (!root) continue;

      if (
        root.querySelector(CART) ||
        root.querySelector(ADD_TO_CART)
      ) {
        customizeAgent(root);
        return true;
      }

      // Suporte para Shadow DOM aninhado
      if (searchShadowRoots(root)) {
        return true;
      }
    }

    return false;
  }

  function findAgent() {
    if (activeRoot?.host?.isConnected) {
      return;
    }

    activeRoot = null;
    searchShadowRoots();
  }

  // ==========================================
  // 10. INICIALIZAR AUTOMATICAMENTE
  // ==========================================

  function initialize() {
    findAgent();

    let pendingScan = false;

    const pageObserver = new MutationObserver(() => {
      if (activeRoot?.host?.isConnected) {
        return;
      }

      if (pendingScan) return;

      pendingScan = true;

      requestAnimationFrame(() => {
        pendingScan = false;
        findAgent();
      });
    });

    pageObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Verificação adicional durante o carregamento
    const retry = setInterval(findAgent, 1000);

    setTimeout(() => {
      clearInterval(retry);
    }, 30000);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();
